import { useQuery } from "@tanstack/react-query";
import { readOnchainWithdrawable } from "@hybrid-escrow/chain";
import { createReadClient } from "../lib/chainClient";
import { usePublicConfig } from "./usePublicConfig";

/**
 * Reads the connected account's `withdrawable` pull-payment balance directly from the escrow
 * contract — normally zero. Nonzero means a prior payout attempt to this address failed (e.g. it
 * was blacklisted by USDC's issuer at that moment) and got credited instead of paid, per
 * `HybridEscrow.sol`'s `_payOut`/`withdraw` docblocks. This is the only way that credit ever
 * reaches the account: nothing else retries it automatically. Read-only and best-effort, same
 * spirit as `useWalletBalances` — never used to infer order/escrow state.
 */
export function useWithdrawableBalance(address: `0x${string}` | null) {
  const { data: config } = usePublicConfig();

  return useQuery({
    queryKey: [
      "withdrawable-balance",
      address,
      config?.chainId,
      config?.rpcUrl,
      config?.escrowContractAddress,
    ],
    queryFn: async (): Promise<bigint> => {
      if (!address || !config) throw new Error("address and public config are required");
      const client = createReadClient(config.chainId, config.rpcUrl);
      return readOnchainWithdrawable(client, config.escrowContractAddress, address);
    },
    enabled: Boolean(address && config),
    staleTime: 20_000,
    refetchInterval: 30_000,
  });
}
